import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClubBranding } from '../context/ClubBrandingContext';
import { useGuestContext } from '../context/GuestIdentityContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dog, ArrowLeft, Loader2, Eye, EyeOff, Ticket, ChevronDown, ChevronUp, Gift, Users, Globe } from 'lucide-react';
import { toast } from 'sonner';
import PromoCodeInput from '../components/PromoCodeInput';

import TurnstileCaptcha from '../components/TurnstileCaptcha';

const API = process.env.REACT_APP_BACKEND_URL;

// Inline agreement content fetcher for the modal
const AgreementContent = () => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    fetch(`${API}/api/beta-agreement`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, []);
  if (!data) return <p className="text-center text-muted-foreground">Loading...</p>;
  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <h3 className="text-sm font-semibold text-blue-900 mb-1.5">Key Points</h3>
        <ul className="space-y-1">
          {data.summary_points.map((pt, i) => (
            <li key={i} className="text-xs text-blue-800 flex gap-1.5"><span className="text-blue-400 shrink-0">*</span>{pt}</li>
          ))}
        </ul>
      </div>
      {data.sections.map((s, i) => (
        <div key={i}>
          <h3 className="font-semibold text-gray-900 text-sm">{s.title}</h3>
          <div className="text-xs text-gray-600 whitespace-pre-line leading-relaxed mt-1">{s.content}</div>
        </div>
      ))}
      <p className="text-[10px] text-gray-400 mt-2">Version {data.version} — Effective {data.effective_date}</p>
    </>
  );
};

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const branding = useClubBranding();
  const { guestId, mergeToUser } = useGuestContext();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const siteName = branding.isWorldPets ? 'WorldPets Global' : 'All American Dog Club';
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      
      // Merge guest session to user after successful login
      if (guestId && result?.user?.id) {
        try {
          await mergeToUser(result.user.id, result.token);
        } catch (err) {
          // Don't fail login if merge fails
          console.debug('Guest merge failed:', err);
        }
      }
      
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <Card className="border-none shadow-hover">
          <CardHeader className="text-center pb-2">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${branding.isWorldPets ? 'bg-emerald-600' : 'bg-primary'}`}>
              {branding.isWorldPets ? (
                <Globe className="w-8 h-8 text-white" />
              ) : (
                <Dog className="w-8 h-8 text-primary-foreground" />
              )}
            </div>
            <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
            <CardDescription>Sign in to continue to {siteName}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="hello@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email"
                  className="input-field"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="login-password"
                    className="input-field pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-to-register">
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const branding = useClubBranding();
  const { guestId, initSession, mergeToUser } = useGuestContext();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: '' });
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [captchaError, setCaptchaError] = useState(false);
  const [validatedPromo, setValidatedPromo] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralSource, setReferralSource] = useState('direct');
  const [betaOptIn, setBetaOptIn] = useState(true);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    displayName: '',
    password: ''
  });
  
  const siteName = branding.isWorldPets ? 'WorldPets Global' : 'All American Dog Club';

  // Check for referral code and UTM source in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    const utmSource = searchParams.get('utm_source') || 'direct';
    
    if (ref) {
      setReferralCode(ref);
      setReferralSource(utmSource);
      
      // Validate referral code
      const validateRef = async () => {
        try {
          const API = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${API}/api/referrals/validate/${ref}`);
          if (res.ok) {
            const data = await res.json();
            setReferralInfo(data);
          }
        } catch (err) {
          console.log('Invalid referral code');
        }
      };
      validateRef();
    }
  }, [searchParams]);

  // Fetch CAPTCHA config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/captcha/config`);
        if (res.ok) {
          const data = await res.json();
          setCaptchaConfig(data);
        }
      } catch (err) {
        console.log('CAPTCHA not configured');
      }
    };
    fetchConfig();
  }, []);

  // Turnstile is now handled by TurnstileCaptcha component
  // Just set loading to false when config is fetched
  useEffect(() => {
    if (!captchaConfig.enabled || !captchaConfig.site_key) {
      setCaptchaLoading(false);
    }
  }, [captchaConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (captchaConfig.enabled && !captchaToken) {
      if (captchaError) {
        toast.error('Verification failed to load. Please refresh the page and try again.');
      } else {
        toast.error('Please complete the verification checkbox');
      }
      return;
    }
    setLoading(true);
    try {
      const promoCode = validatedPromo?.code || null;
      
      const result = await register(
        formData.email, 
        formData.username, 
        formData.displayName, 
        formData.password,
        captchaToken,
        promoCode,
        referralCode || null,
        referralSource || 'direct',
        guestId  // Use the guestId from context
      );
      
      // Accept beta agreement if opted in
      if (betaOptIn && result.token) {
        try {
          const API = process.env.REACT_APP_BACKEND_URL;
          await fetch(`${API}/api/beta-agreement/accept`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${result.token}`
            },
            body: JSON.stringify({ version: '1.0', accepted: true })
          });
        } catch (err) {
          // Don't fail registration if beta acceptance fails
        }
      }
      
      // Merge guest session to user after successful registration
      if (guestId && result?.user?.id) {
        try {
          await mergeToUser(result.user.id, result.token);
        } catch (err) {
          // Don't fail registration if merge fails
          console.debug('Guest merge failed:', err);
        }
      }
      
      // Show appropriate success message based on promo and referral
      if (result.referralApplied) {
        toast.success(`Welcome! You'll get 50% off when you upgrade, thanks to ${result.referralApplied.referrer}!`);
      } else if (result.promoApplied) {
        const promo = result.promoApplied;
        if (promo.type === 'free_trial') {
          toast.success(`Welcome! You have ${promo.days} days of Full membership free!`);
        } else if (promo.type === 'free_upgrade') {
          toast.success(`Welcome! You have ${promo.days} days of Premium membership free!`);
        } else {
          toast.success(`Welcome to ${siteName}! Promo code ${promo.code} applied.`);
        }
      } else {
        toast.success(`Welcome to ${siteName}!`);
      }
      
      navigate('/');  // Redirect to member dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      // Reset CAPTCHA on error
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <Card className="border-none shadow-hover">
          <CardHeader className="text-center pb-2">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${branding.isWorldPets ? 'bg-emerald-600' : 'bg-primary'}`}>
              {branding.isWorldPets ? (
                <Globe className="w-8 h-8 text-white" />
              ) : (
                <Dog className="w-8 h-8 text-primary-foreground" />
              )}
            </div>
            <CardTitle className="font-heading text-2xl">Join {siteName}</CardTitle>
            <CardDescription>Create your account to get started</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Referral Banner */}
            {referralInfo && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="referral-banner">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Gift className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-700">
                      Referred by {referralInfo.referrer}!
                    </p>
                    <p className="text-sm text-green-600">
                      {referralInfo.your_benefit}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="hello@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email"
                  className="input-field"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="doglover"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                    required
                    data-testid="register-username"
                    className="input-field"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Dog Lover"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    required
                    data-testid="register-displayname"
                    className="input-field"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password"
                    className="input-field pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">At least 6 characters</p>
              </div>

              {/* Promo Code Section */}
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowPromoCode(!showPromoCode)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
                  data-testid="toggle-promo-code"
                >
                  <Ticket className="w-4 h-4" />
                  Have a promo code?
                  {showPromoCode ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                </button>
                
                {showPromoCode && (
                  <div className="mt-3" data-testid="promo-code-section">
                    <PromoCodeInput
                      scope="membership"
                      onValidCode={(data) => setValidatedPromo(data)}
                      onClear={() => setValidatedPromo(null)}
                      label=""
                      placeholder="Enter promo code"
                    />
                    {validatedPromo && (
                      <p className="text-xs text-green-600 mt-2">
                        Code will be applied to your new account!
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Beta Member Agreement */}
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-2" data-testid="beta-agreement-section">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={betaOptIn}
                    onChange={e => setBetaOptIn(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="beta-opt-in-checkbox"
                  />
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">Join as a Beta Member</span>
                    <span className="text-gray-600"> — early access, founding member status, and shape the platform</span>
                  </div>
                </label>
                <div className="pl-[26px] text-xs text-gray-500 space-y-1">
                  <p>* All policies including pricing subject to change during beta</p>
                  <p>* Platform provided as-is, no uptime guarantees</p>
                  <p>* Your feedback helps us build what matters</p>
                  <button
                    type="button"
                    onClick={() => setShowAgreementModal(true)}
                    className="text-blue-600 hover:text-blue-700 font-medium underline"
                    data-testid="read-agreement-btn"
                  >
                    Read full Beta Member Agreement
                  </button>
                </div>
                {!betaOptIn && (
                  <p className="pl-[26px] text-xs text-amber-600">
                    You can still create an account, but won't receive beta member benefits.
                  </p>
                )}
              </div>

              {/* Beta Agreement Modal */}
              {showAgreementModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAgreementModal(false)}>
                  <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="agreement-modal">
                    <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
                      <h2 className="text-lg font-semibold">Beta Member Agreement</h2>
                      <button onClick={() => setShowAgreementModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                    </div>
                    <div className="overflow-y-auto px-6 py-4 space-y-4 text-sm text-gray-700">
                      <AgreementContent />
                    </div>
                    <div className="px-6 py-3 border-t shrink-0 flex justify-end">
                      <Button size="sm" onClick={() => setShowAgreementModal(false)}>Close</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* CAPTCHA Widget - Using improved mobile-friendly component */}
              {captchaConfig.enabled && captchaConfig.site_key && (
                <div className="flex flex-col items-center py-2" data-testid="captcha-container">
                  <TurnstileCaptcha
                    siteKey={captchaConfig.site_key}
                    onVerify={async (token) => {
                      setCaptchaToken(token);
                      setCaptchaLoading(false);
                      setCaptchaError(false);
                      
                      // Initialize guest session with CAPTCHA token
                      try {
                        await initSession(token);
                      } catch (err) {
                        console.debug('Guest session init failed:', err);
                      }
                    }}
                    onError={(error) => {
                      console.error('Turnstile error:', error);
                      setCaptchaToken('');
                      setCaptchaLoading(false);
                      setCaptchaError(true);
                    }}
                    onExpire={() => {
                      setCaptchaToken('');
                      setCaptchaError(false);
                    }}
                    action="register"
                    theme="light"
                  />
                  {!captchaToken && !captchaError && !captchaLoading && (
                    <p className="text-xs text-muted-foreground mt-2">Please complete the verification above</p>
                  )}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
